import { Fragment } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import classnames from 'classnames';

import styles from './Breadcrumbs.module.css';

import { Arrow } from '../../icons';
import { Text } from '../Text';
import { splitPath } from './utils/splitPath';
import { stringifyUrl } from './utils/stringifyUrl';

export interface BreadcrumbsProps {
  /**
   * The base path to exclude from the breadcrumbs.
   *
   * E.g.: `/app/foo/bar` with `stem="/app"` will reduce the breadcrumbs to
   * `/foo/bar`
   */
  stem: string;
  /** A function that will stringify the url to a display string. */
  stringify?: (url: string) => string;
  /** Current pathname (for demo purposes only). */
  pathname?: string;
  /** A class name for the breadcrumb container/ */
  containerClassName?: string;
}

/**
 * Render a list of breadcrumbs links.
 *
 * @see https://corgi-x.tfd.engineering/components/breadcrumbs
 */
export function Breadcrumbs({
  stringify = stringifyUrl,
  pathname: pathnameProp,
  stem = '',
  containerClassName,
  ...props
}: BreadcrumbsProps) {
  const location = useLocation();
  const pathname = pathnameProp || location.pathname;
  const breadcrumbs = splitPath(pathname);
  const stems = splitPath(stem);
  const className = classnames(styles.root, containerClassName);
  return (
    <Text as="div" className={className}>
      {breadcrumbs.map((breadcrumb, index) => {
        const previousBreadcrumbs = breadcrumbs.slice(0, index + 1);
        const isLast = index === breadcrumbs.length - 1;

        if (stems.includes(breadcrumb)) {
          return <></>;
        }

        const path = `/${previousBreadcrumbs.join('/')}`;

        return (
          <Fragment key={path}>
            {!isLast ? (
              <>
                <NavLink {...props} to={path}>
                  {stringify(breadcrumb)}
                </NavLink>
                <Arrow arrowDirection="right" className={styles.caret} />
              </>
            ) : (
              stringify(breadcrumb)
            )}
          </Fragment>
        );
      })}
    </Text>
  );
}
